// src/components/PersonalDataModal.js
import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { db, paths } from '../firebase/config';
import { doc, updateDoc } from "firebase/firestore";
import { logAuditoria } from '../context/AuthContext';

export const PersonalDataModal = ({ show, onClose, userProfile }) => {
    const [formData, setFormData] = useState({
        nome_completo: '',
        nome: '', // QRA
        matricula: '' // RE
    });
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (show && userProfile) {
            setFormData({
                nome_completo: userProfile.nome_completo || '',
                nome: userProfile.nome || '',
                matricula: userProfile.matricula || ''
            });
            setError('');
            setSuccess('');
        }
    }, [show, userProfile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Lógica específica para o campo 'matricula' (RE)
        if (name === 'matricula') {
            // Converte para maiúsculas e remove tudo que não for letra ou número
            let raw = value.toUpperCase().replace(/[^0-9A-Z]/g, '');
            
            // Separa os primeiros 6 caracteres (que devem ser números)
            let numbersPart = raw.slice(0, 6).replace(/\D/g, ''); 
            
            // Pega o dígito verificador (pode ser letra ou número), se existir
            let digitPart = raw.slice(6, 7); 
            
            // Reconstrói o valor
            let finalValue = numbersPart;
            if (raw.length > 6) {
                finalValue += digitPart;
            }

            // Aplica a máscara XXXXXX-Y se tiver o dígito
            if (finalValue.length > 6) {
                finalValue = finalValue.replace(/^(\d{6})(.+)/, '$1-$2');
            }
            
            setFormData(prev => ({ ...prev, [name]: finalValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            if (!formData.nome || !formData.matricula) {
                throw new Error("QRA e RE são obrigatórios.");
            }
            
            // Validação do formato do RE ao salvar
            // Limpa formatação para validar
            const reLimpo = formData.matricula.replace(/[^0-9A-Z]/g, '');
            
            // Verifica tamanho (6 números + 1 dígito = 7 chars)
            if (reLimpo.length !== 7) {
                throw new Error("O RE deve conter exatamente 7 caracteres (6 números e 1 dígito/letra). Ex: 123456-A");
            }
            
            // Garante que os primeiros 6 sejam números
            if (!/^\d{6}/.test(reLimpo)) {
                 throw new Error("Os primeiros 6 caracteres do RE devem ser números.");
            }

            const userRef = doc(db, paths.users, userProfile.id);
            
            await updateDoc(userRef, {
                nome_completo: formData.nome_completo,
                nome: formData.nome,
                matricula: formData.matricula // Salva com a formatação (ex: 123456-A)
            });

            await logAuditoria(userProfile, "Atualização de Dados Pessoais", {
                antigo: { qra: userProfile.nome, re: userProfile.matricula },
                novo: formData
            });

            setSuccess("Dados atualizados com sucesso! As alterações aparecerão no próximo login ou recarregamento.");
            
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (err) {
            console.error("Erro ao atualizar dados:", err);
            setError("Erro ao salvar: " + err.message);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Modal show={show} onClose={() => !processing && onClose()} title="Atualizar Dados Pessoais" size="md">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Nome Completo</label>
                    <input
                        name="nome_completo"
                        value={formData.nome_completo}
                        onChange={handleChange}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Ex: João da Silva"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">QRA (Nome de Guerra)</label>
                    <input
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Ex: Cad PM Silva"
                        required
                    />
                    <p className="text-xs text-gray-500 mt-1">Este é o nome que aparecerá nas escalas.</p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">RE (Registro Estatístico)</label>
                    <input
                        name="matricula"
                        value={formData.matricula}
                        onChange={handleChange}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none uppercase"
                        placeholder="123456-1 ou 123456-A" 
                        maxLength="8" // 7 caracteres + 1 hífen
                        required
                    />
                </div>

                {error && <div className="p-2 bg-red-100 text-red-700 text-sm rounded">{error}</div>}
                {success && <div className="p-2 bg-green-100 text-green-700 text-sm rounded">{success}</div>}

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" onClick={onClose} variant="secondary" disabled={processing}>
                        Cancelar
                    </Button>
                    <Button type="submit" variant="primary" disabled={processing}>
                        {processing ? "Salvando..." : "Salvar Alterações"}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};